/**
 * What transition a timeline row records — and the three answers that are not one.
 *
 * ── Why this is a function and not a `?? ''` in a `<li>` ────────────────────
 *
 * G19 put `from_state` and `to_state` on `IncidentComment`. Both are **required and
 * nullable**: the server always sends them, and `null` means **this row records no
 * state change** — a note, an evidence entry, the `system` row that opens an
 * incident. It does not mean unknown, and it does not mean "the server did not say".
 *
 * A component that reached for `comment.to_state ?? '—'` would render the same dash
 * for a note and for a broken pair, and a reader would have no way to tell a quiet
 * incident from a record with a hole in it. That is the collapse this build has a
 * standing rule about, arriving on the one panel whose entire job is to be the
 * record. So the reading is a discriminated union, driven directly and mutated one
 * branch at a time.
 *
 * ── The three answers ───────────────────────────────────────────────────────
 *
 *  - **`transition`** — both present. The row is a move, and the pair is printed.
 *    `known` says whether *this client* has a label for both names: a server one
 *    state ahead of this bundle must still show the move, with the raw identifiers,
 *    rather than hiding a real transition behind a client that is behind.
 *  - **`none`** — both `null`. The row is a note or an evidence entry and records no
 *    move. A positive sentence, not a blank.
 *  - **`half_pair`** — exactly one is `null`. `wp_incident_history_guard` refuses to
 *    commit a status change that does not write both, so this cannot arise from a
 *    healthy server; it is kept as its own answer rather than folded into either
 *    neighbour precisely because if it ever *does* arrive it means the guard or the
 *    renderer is broken, and silently reading it as "a note" would hide that on the
 *    audit surface. Fail closed, say what came.
 *
 * There is deliberately **no fourth branch for `from === to`.** `openapi.yaml` says
 * it never happens — writing the same status back is a no-op that leaves no history
 * row — and a branch nothing can reach is a branch nothing tests.
 */

import { isIncidentState, type IncidentState } from './state-machine';
import type { IncidentComment } from '../api/types';

export type TimelineTransition =
  | {
      readonly kind: 'transition';
      readonly from: string;
      readonly to: string;
      /** Both names are in this client's own vocabulary, so both have a label. */
      readonly known: boolean;
    }
  | { readonly kind: 'none' }
  | { readonly kind: 'half_pair'; readonly from: string | null; readonly to: string | null };

export function transitionOf(comment: IncidentComment): TimelineTransition {
  const from = comment.from_state;
  const to = comment.to_state;
  if (from === null && to === null) return { kind: 'none' };
  if (from === null || to === null) return { kind: 'half_pair', from, to };
  return { kind: 'transition', from, to, known: isIncidentState(from) && isIncidentState(to) };
}

/** The pair as this client's own states, when it has labels for both. */
export function knownPair(
  transition: TimelineTransition,
): { readonly from: IncidentState; readonly to: IncidentState } | null {
  if (transition.kind !== 'transition' || !transition.known) return null;
  return { from: transition.from as IncidentState, to: transition.to as IncidentState };
}
